import clsx from 'clsx'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getIndexFromLineCol } from '../../utils/getIndexFromLineCol'
import { validateJson as validateJsonUtil } from '../../utils/jsonUtils'

import { Button, Flex, Typography } from '@percent/lemonade'
import styles from './JsonEditor.module.scss'

interface JsonEditorProps {
  editedJson: string
  onJsonChange: (value: string) => void
  onUpdate: () => void
  isUpdating: boolean
}

interface JsonErrorPosition {
  line: number
  column: number
}

export function JsonEditor({ editedJson, onJsonChange, onUpdate, isUpdating }: Readonly<JsonEditorProps>) {
  const { t } = useTranslation()
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [jsonErrorPos, setJsonErrorPos] = useState<JsonErrorPosition | null>(null)
  const codeEditorRef = useRef<HTMLTextAreaElement | null>(null)
  const codeGutterRef = useRef<HTMLPreElement | null>(null)

  const validateJson = useCallback(
    (value: string): { isValid: boolean; error?: string; position?: JsonErrorPosition } => {
      const errorMessage = t('stateMachine.error.wrongFormat')

      return validateJsonUtil(value, errorMessage)
    },
    [t]
  )

  const handleJsonChange = useCallback(
    (value: string) => {
      onJsonChange(value)

      const validation = validateJson(value)

      if (validation.isValid) {
        setJsonError(null)
        setJsonErrorPos(null)
      } else {
        setJsonError(validation.error || null)
        setJsonErrorPos(validation.position || null)
      }
    },
    [onJsonChange, validateJson]
  )

  const scrollToError = useCallback(() => {
    if (!jsonErrorPos || !codeEditorRef.current) return

    const textarea = codeEditorRef.current
    const index = getIndexFromLineCol(editedJson, jsonErrorPos.line, jsonErrorPos.column)

    textarea.focus()
    textarea.setSelectionRange(index, index)

    const lineHeightRaw = window.getComputedStyle(textarea).lineHeight
    const lineHeight = Number.parseInt(lineHeightRaw || '18', 10) || 18
    const targetTop = Math.max(0, (jsonErrorPos.line - 1) * lineHeight - 40)

    textarea.scrollTop = targetTop
  }, [jsonErrorPos, editedJson])

  const handleScroll = useCallback((event: React.UIEvent<HTMLTextAreaElement>) => {
    const { scrollTop } = event.target as HTMLTextAreaElement

    if (codeGutterRef.current) {
      codeGutterRef.current.scrollTop = scrollTop
    }
  }, [])

  const hasErrors = Boolean(jsonError)

  return (
    <Flex direction="column" gap="xs" className={styles.jsonEditor}>
      <div className={styles.updateButtonContainer}>
        <Button size="small" variant="primary" disabled={hasErrors || isUpdating} onClick={onUpdate}>
          {t('button.updateStateMachine')}
        </Button>
      </div>

      {jsonError && (
        <Typography
          variant="small"
          style={{
            color: '#ef4444',
            marginTop: 4,
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
          onClick={scrollToError}
        >
          {jsonError}
          {jsonErrorPos && ` (line ${jsonErrorPos.line}, column ${jsonErrorPos.column})`}
        </Typography>
      )}

      <Flex className={styles.editorContainer}>
        <pre ref={codeGutterRef} className={styles.editorGutter} aria-hidden>
          {editedJson.split('\n').map((_, idx) => (
            <div
              key={`ln-${idx + 1}`}
              style={{
                color: jsonErrorPos?.line === idx + 1 ? '#ef4444' : undefined
              }}
            >
              {idx + 1}
            </div>
          ))}
        </pre>
        <textarea
          className={clsx(styles.editorTextarea, hasErrors && styles.jsonErrorHighlight)}
          value={editedJson}
          onChange={e => handleJsonChange(e.target.value)}
          onScroll={handleScroll}
          ref={codeEditorRef}
        />
      </Flex>
    </Flex>
  )
}
